/**
 * Incident Reporting Service
 *
 * Sends crash/bug reports to the server, which emails them to anything@tarr.ink.
 * Used by:
 *   - ErrorBoundary (auto-report on crash)
 *   - ReportIssueModal (manual user report)
 *   - Global error handlers (unhandled errors/rejections)
 */

import API_BASE_URL from '../config/api';

const INCIDENT_ENDPOINT = `${API_BASE_URL}/api/incidents/report`;

// Debounce: don't send more than 1 auto-report per 30 seconds
let lastAutoReportTime = 0;
const AUTO_REPORT_COOLDOWN = 30000;

/**
 * Report an incident to the server.
 *
 * @param {Object} params
 * @param {string} params.type         - 'crash' | 'bug' | 'feedback'
 * @param {string} params.severity     - 'critical' | 'high' | 'medium' | 'low'
 * @param {string} [params.description] - User-written description
 * @param {string} [params.errorMessage] - JS error .message
 * @param {string} [params.stackTrace]   - JS error .stack
 * @param {string} [params.componentStack] - React componentDidCatch stack
 * @param {Object} [params.metadata]     - Extra context
 * @returns {Promise<{success: boolean}>}
 */
export async function reportIncident({
  type = 'crash',
  severity = 'high',
  description = '',
  errorMessage = '',
  stackTrace = '',
  componentStack = '',
  metadata = {},
}) {
  // Get user info from localStorage (best-effort, may not be available)
  let userEmail = '';
  try {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    userEmail = currentUser.email || '';
  } catch {
    // noop
  }

  const payload = {
    type,
    severity,
    description,
    errorMessage,
    stackTrace,
    componentStack,
    pageUrl: window.location.href,
    userAgent: navigator.userAgent,
    userEmail,
    metadata: {
      ...metadata,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timestamp: new Date().toISOString(),
    },
  };

  try {
    // Intentionally using raw fetch (not apiClient) — incident reports must work
    // even when auth state is broken (e.g., crash before login, expired token).
    const response = await fetch(INCIDENT_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    return { success: data.success };
  } catch {
    // Incident reporting should never throw
    return { success: false };
  }
}

/**
 * Auto-report a crash (called by ErrorBoundary / global error handlers).
 * Rate-limited to avoid spam.
 */
export async function autoReportCrash(error, componentStack = '') {
  const now = Date.now();
  if (now - lastAutoReportTime < AUTO_REPORT_COOLDOWN) return;
  lastAutoReportTime = now;

  return reportIncident({
    type: 'crash',
    severity: 'critical',
    errorMessage: error?.message || String(error),
    stackTrace: error?.stack || '',
    componentStack,
  });
}


